import type { CheckoutSession } from "@/types";
import { encodeCurrency } from "@/lib/codec";
import { getResolvedCheckoutId } from "@/lib/checkout-id";
import { getQuerystring } from "@/lib/web";
import { getCheckoutUserIdCookie } from "@/services/cookie";

import { browserAgent } from "@/shared/telemetry/newrelic";

import { getTracker, trackSessionEvent } from "./analytics-helpers";

export function emitErrorTracking(
  eventType: string,
  session: CheckoutSession,
  eventData: Record<string, unknown>,
): void {
  trackSessionEvent(eventType, session, eventData);
}

export function emitPageLoadTracking(): void {
  const tracker = getTracker();
  const checkoutId = getResolvedCheckoutId();

  if (!tracker || !checkoutId) {
    return;
  }

  tracker.track({
    eventType: "page_loaded",
    merchantId: "",
    checkoutId,
    orderId: "",
    customerId: "",
    countryCode: "",
    currencyCode: "",
    eventData: {},
  });
}

export function emitPageUnloadTracking(
  unloadReason: string,
  nextUrl: string | null,
  session?: CheckoutSession,
): void {
  const tracker = getTracker();
  if (!tracker) {
    return;
  }

  const checkoutId = getResolvedCheckoutId() ?? session?.id ?? "";
  const orderIdFromUrl = getQuerystring(window, "orderId");
  const customerIdFromCookie = getCheckoutUserIdCookie();

  const orderId = orderIdFromUrl ?? session?.order?.id ?? "";
  const merchantId = session?.merchant?.id ?? "";
  const customerId = customerIdFromCookie ?? session?.customerId ?? "";
  const countryCode = session?.order?.countryCode ?? "";
  const currencyCode = session?.order?.currency
    ? (encodeCurrency(session.order.currency) ?? "SAR")
    : "SAR";

  const currentUrl = window.location.href;
  const referrer = document.referrer || "";
  const pathname = window.location.pathname;
  const search = window.location.search;

  tracker.track({
    eventType: "page_unload",
    merchantId,
    checkoutId,
    orderId,
    customerId,
    countryCode,
    currencyCode,
    eventData: {
      unload_reason: unloadReason,
      current_url: currentUrl,
      referrer,
      next_url: nextUrl,
      unload_timestamp: Date.now(),
      pathname,
      search,
    },
  });
}

export function emitModalTracking(
  eventType:
    | "modal_opened"
    | "modal_closed"
    | "cvv_modal_opened"
    | "cvv_modal_closed"
    | "documents_modal_opened"
    | "documents_modal_closed"
    | "order_details_modal_opened"
    | "order_details_modal_closed"
    | "card_form_modal_opened"
    | "card_form_modal_closed",
  session: CheckoutSession,
  eventData?: {
    modal_name?: string;
    time_spent_ms?: number;
  },
): void {
  trackSessionEvent(eventType, session, eventData ?? {});
}

export function emitNavigationTracking(
  eventType:
    | "back_button_clicked"
    | "navigation_attempted"
    | "page_exit_prevented",
  session: CheckoutSession,
  eventData?: {
    from_page?: string;
    to_page?: string;
    prevent_reason?: string;
  },
): void {
  trackSessionEvent(eventType, session, eventData ?? {});
}

export function emitFormInteractionTracking(
  eventType:
    | "form_field_focused"
    | "form_field_blurred"
    | "form_validation_error"
    | "cvv_entered",
  session: CheckoutSession,
  eventData?: {
    field_name?: string;
    validation_errors?: string[];
  },
): void {
  trackSessionEvent(eventType, session, eventData ?? {});
}

type DocumentEventType =
  | "finance_documents_viewed"
  | "finance_document_opened"
  | "terms_agreed"
  | "terms_disagreed"
  | "document_view_time";

/**
 * New Relic PageAction names for document/compliance events.
 *
 * Spelled out as literals rather than derived from the event type: compliance
 * alerting queries these by name, so they must stay greppable and must not
 * silently change if an event type is ever renamed.
 */
const DOCUMENT_PAGE_ACTIONS: Record<DocumentEventType, string> = {
  finance_documents_viewed: "FINANCE_DOCUMENTS_VIEWED",
  finance_document_opened: "FINANCE_DOCUMENT_OPENED",
  terms_agreed: "TERMS_AGREED",
  terms_disagreed: "TERMS_DISAGREED",
  document_view_time: "DOCUMENT_VIEW_TIME",
};

export function emitDocumentTracking(
  eventType: DocumentEventType,
  session: CheckoutSession,
  eventData?: {
    document_id?: string;
    document_type?: string;
    view_time_ms?: number;
  },
): void {
  const payload = eventData ?? {};
  trackSessionEvent(eventType, session, payload);
  browserAgent.addPageAction(DOCUMENT_PAGE_ACTIONS[eventType], {
    ...payload,
    checkout_id: session.id,
    order_id: session.orderId ?? session.order?.id ?? "",
    customer_id: session.customerId ?? "",
    merchant_id: session.merchant?.id ?? "",
    country_code: session.order?.countryCode ?? "",
  });
}

export interface UndefinedBehaviorEventData {
  http_status: number;
  http_method: string;
  endpoint: string;
  error_code?: string;
  error_message?: string;
  frontend_session_status?: string;
  recovery_action: "refetch_session" | "show_error_screen" | "page_refresh";
  recovery_successful?: boolean;
  request_context?: Record<string, unknown>;
}

export function emitUndefinedBehaviorTracking(
  eventData: UndefinedBehaviorEventData,
  session?: {
    merchant?: { id?: string };
    id?: string;
    order?: { id?: string; countryCode?: string; currency?: unknown };
    customerId?: string;
  },
): void {
  const tracker = getTracker();

  const checkoutId =
    session?.id || getResolvedCheckoutId() || "unknown";
  const customerId =
    session?.customerId || getCheckoutUserIdCookie() || "unknown";

  if (!tracker) {
    console.warn("[UndefinedBehavior]", {
      checkoutId,
      ...eventData,
    });
    return;
  }

  const merchantId = session?.merchant?.id || "unknown";
  const orderId = session?.order?.id || "unknown";
  const countryCode = session?.order?.countryCode || "unknown";
  const currencyCode = session?.order?.currency
    ? (encodeCurrency(
        session.order.currency as Parameters<typeof encodeCurrency>[0],
      ) ?? "SAR")
    : "SAR";

  tracker.track({
    eventType: "undefined_behavior",
    merchantId,
    checkoutId,
    orderId,
    customerId,
    countryCode,
    currencyCode,
    eventData: {
      ...eventData,
      timestamp: new Date().toISOString(),
      user_agent:
        typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
      page_url:
        typeof window !== "undefined" ? window.location.href : "unknown",
    },
  });
}
